import { OmniaConfig } from "../config"
import { omniaResolve } from "../promises"
import { IOmniaHttpService } from "../types"
import { isObject } from "../utils/utils"
import { defaultLanguage, getSupportedLanguages } from "./languages"
import { getMessageKey, loadMessages, MsgObject, MsgTemplateArguments, normalizeWhitespace, parseMessage } from "./messages"
import { RegionsFile } from "./regions"
import { I18nService, LangFile, Message, ResourceIndex, ResourceType } from "./types"

export class I18nServiceImpl implements I18nService {

    private init = false
    private loading: PromiseLike<void>


    private lang: string
    private defaultLang: boolean = true
    private messages: Map<string, Message>

    private resources: ResourceIndex

    private regions: RegionsFile

    static $inject = ["OmniaHttpService", "omniaConfig"]

    constructor(
        private readonly http: IOmniaHttpService,
        private readonly config: OmniaConfig,
        private readonly enabled = config.i18n
    ) {

        this.loadInitialLocale()

    }

    getLanguage() {
        return this.lang
    }

    usesDefaultLanguage() {
        return this.defaultLang
    }

    private loadInitialLocale() {

        if (!this.enabled) {
            this.regions = undefined
            this.loadDefaultLanguage()
            return
        }
        const lang = getInitialLanguage()

        this.loadRegions()
            .then(() => this.loadLocale(lang, true))
            .then(() => this.init = true)

    }


    loadRegions(): PromiseLike<void> {

        const regions = this.fetch(`resources/regions.json`)

        return regions.then(r => {
            this.regions = r as RegionsFile
        })
    }

    loadLocale(lang: string, local: boolean = false): PromiseLike<void> {

        if ((lang ?? defaultLanguage) === defaultLanguage) {
            this.loadDefaultLanguage()
            return omniaResolve()
        }

        const fetchLocalFile = () => this.fetch<LangFile>(`resources/${lang}/text.json`)

        const p: PromiseLike<LangFile> = local
            ? fetchLocalFile()
            : this.http.get(`/api/intl/v1/text/language/${lang}`)
                .then(r => r.data)
                .catch(() => fetchLocalFile())

        const r = this.loading = p.then(r => {

            this.lang = lang
            this.defaultLang = false

            this.messages = r.messages
                ? loadMessages(r.messages)
                : new Map()

            this.resources = r.resources ?? {}

            this.loading = undefined

        })

        return r

    }

    private loadDefaultLanguage() {
        this.lang = defaultLanguage
        this.messages = undefined
        this.loading = undefined
        this.resources = undefined
        this.defaultLang = true
    }

    ensureInitialized() {

        const { init, loading } = this

        return loading && (!init)
            ? loading
            : omniaResolve<void>()

    }

    getMessage(arg: string | MsgObject) {

        let found = false
        let { id: msg, context, locals, text } = parseMessage(arg)

        if (text) {
            msg = text
            found = true
        }

        else {

            if (!msg) {
                return { msg: undefined, found: false }
            }


            const { messages } = this

            if (!messages) {

                found = true

            }
            else {

                const get = (id: string) => {
                    const key = getMessageKey({ id, context })
                    return messages.get(key)
                }

                const match = get(msg) ?? get(normalizeWhitespace(msg))

                if (match) {
                    const str = match.str
                    if (str !== undefined) {
                        msg = str
                        found = true
                    }
                }

            }

        }

        msg = this.formatMessage(msg, locals)

        return { msg, found }

    }


    formatMessage(msg: string, locals?: MsgTemplateArguments) {

        if (msg && locals && isObject(locals)) {

            for (const [key, value] of Object.entries(locals)) {
                const re = new RegExp("\{\{" + key + "\}\}", "ig")
                msg = msg.replace(re, value.toString())
            }

        }

        if (msg) {

            const app = /\(@brand\)/g
            msg = msg.replace(app, this.config.brand.name)

            const terramap = /\(@terramap\)/g
            msg = msg.replace(terramap, this.config.brand.terramap)

        }

        return msg

    }

    getResources(type: ResourceType) {

        const { resources } = this
        return resources
            ? resources[type] ?? []
            : []

    }


    getRegions() {

        return this.regions.regions

    }

    getRegion(regionId: number) {

        const { regions: { regions, countries } } = this

        const region = regions?.find(r => r.regionId === regionId)
        const country = countries?.find(c => c.code === region?.country)

        return { region, country }

    }

    getTimezones(countryCode?: string) {

        const zones = this.regions?.timezones ?? []

        return countryCode
            ? zones.filter(z => z.country === countryCode)
            : zones

    }

    private fetch<T>(url: string) {
        return this.http.getExternal<T>(url, false)
    }


}


function getInitialLanguage() {

    const supported = new Set(Object.keys(getSupportedLanguages()))

    const p = new URLSearchParams(location.search.substring(1))

    let lang = p.get("lang")

    if (!lang) {

        lang = navigator.language

        if (!supported.has(lang)) {
            lang = lang.split('-')[0]
        }

        if (!supported.has(lang)) {
            lang = undefined
        }

    }

    return lang ?? defaultLanguage

}
